<script setup lang="ts">
import 'lazysizes'

if (import.meta.browser) {
  lazySizes.cfg.preloadAfterLoad = true
}

export interface Props {
  image: Media | MediaCover
  lazy?: boolean,
  addLazyLoadOnMount?: boolean
  addLazyLoadManually?: boolean
  applyAspectRatioStyle?: boolean,
  useOwnAspectRatio?: boolean,
}

const props = withDefaults(defineProps<Props>(), {
  lazy: true,
  addLazyLoadOnMount: true,
  addLazyLoadManually: false,
  applyAspectRatioStyle: true,
  useOwnAspectRatio: true,
})

const emit = defineEmits<{
  loaded: []
}>()

const imgRef = useTemplateRef<HTMLImageElement>('imgRef')
const addLazyLoadClass = ref(false)

onMounted(() => {
  if (props?.addLazyLoadOnMount) { addLazyLoadClass.value = true }

  imgRef.value?.addEventListener('lazyloaded', () => {
    emit('loaded')
  })
})

const classList = computed(() => {
  if (props?.lazy === false) {
    return ''
  }
  return [
    props?.addLazyLoadManually || addLazyLoadClass.value ? 'lazyload' : null,
  ]
})

const aspectRatio = computed(() => {
  if (!props.applyAspectRatioStyle) { return undefined }
  if (props.image.width && props.image.height) {
    if (props.useOwnAspectRatio) {
      return `${props.image.width} / ${props.image.height}`
    } else {
      switch (getOrientation(props.image.width, props.image.height)) {
        case 'landscape':
          return '16 / 11'
        case 'portrait':
          return '3 / 4'
        case 'square':
          return '1 / 1'
      }
    }
  }
  else {
    return undefined
  }
})

// 1x1 transparent gif: gives the <img> a valid source so the browser paints the
// element's background instead of the broken-image/alt-text fallback
const PLACEHOLDER = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'

const srcset = Object.values(props.image?.sizes || {}).map(size => {
  return `${size.url} ${size.width}w`
}).join(', ')
</script>

<style scoped>
.root[data-lazy]:not(.lazyloaded) {
  background-color: var(--color--accent, var(--color--gray));
  /* hide the alt text / broken-image glyph while the real source loads */
  color: transparent;
  font-size: 0;
  text-indent: -9999px;
}
</style>

<template>
  <img
    ref="imgRef"
    :key="image.id"
    class="root w-full"
    :data-lazy="lazy ? '' : null"
    :class="classList"
    :src="lazy ? PLACEHOLDER : undefined"
    :data-srcset="lazy ? srcset : null"
    :srcset="!lazy ? srcset : null"
    :alt="image?.alt || image?.filename"
    data-sizes="auto"
    :data-orientation="image?.orientation"
    :style="{
      'aspect-ratio': aspectRatio,
    }"
  >
</template>
